import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { RELEASE_VERSION } from '@/data/releaseNotes';
import { visibleCategories, capAllowed } from '@/config/adminNav';
import {
  Search, ChevronRight, Users, Building2, ListChecks, Activity, UserPlus,
  Award, Bell, Cpu, DollarSign, Clock, GitBranch, Rocket, ArrowRight, Shield,
  KeyRound, Bot, Mail, ScrollText, Sparkles, HardDrive,
} from 'lucide-react';

// ── Quick actions (role-gated) ─────────────────────────────────────────────
const QUICK_ACTIONS = [
  { label: 'Invite User', to: '/access-control?tab=users', icon: UserPlus, cap: 'users:write' },
  { label: 'Create Role', to: '/access-control?tab=roles', icon: Shield, cap: 'roles:write' },
  { label: 'Add AI Provider', to: '/admin/llm', icon: Bot, cap: 'users:read' },
  { label: 'Object Storage', to: '/admin/storage', icon: HardDrive, cap: 'platform:operations' },
  { label: 'Upload SSO Metadata', to: '/admin/sso', icon: KeyRound, cap: 'users:write' },
  { label: 'Email-to-Insight', to: '/admin/inbound-emails', icon: Mail, cap: 'users:read' },
  { label: 'Create Workspace', to: '/admin/tenants', icon: Building2, cap: 'platform:customers' },
  { label: 'Restart Jobs', to: '/admin/jobs', icon: ListChecks, cap: 'platform:operations' },
  { label: 'View System Logs', to: '/system-logs', icon: ScrollText, cap: 'platform:operations' },
  { label: 'Demo Environment', to: '/admin/demo-env', icon: Sparkles, cap: 'platform:operations' },
];

function Widget({ icon: Icon, label, value, sub, to, accent = 'text-primary', loading }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(to)}
      className="text-left bg-card/60 border border-border/40 rounded-2xl p-4 hover:border-primary/40 hover:bg-card transition-all duration-200 group"
      data-testid={`admin-widget-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className={`w-9 h-9 rounded-xl bg-muted/40 flex items-center justify-center ${accent}`}>
          <Icon className="w-4.5 h-4.5" strokeWidth={1.75} />
        </div>
        <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <div className="text-2xl font-heading font-bold text-foreground tabular-nums">
        {loading ? <span className="inline-block w-10 h-6 rounded bg-muted/50 animate-pulse" /> : value}
      </div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{label}</div>
      {sub ? <div className="text-[10px] text-muted-foreground/70 mt-1">{sub}</div> : null}
    </button>
  );
}

export default function AdministrationHubPage() {
  const { user, hasPermission } = useAuth();
  const [query, setQuery] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const categories = useMemo(() => visibleCategories(user, hasPermission), [user, hasPermission]);
  const quickActions = useMemo(() => QUICK_ACTIONS.filter((a) => capAllowed(a, user, hasPermission)), [user, hasPermission]);

  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      api.getSystemHealth(),
      api.getCertificationQueue(),
      api.getDeployStatus ? api.getDeployStatus() : Promise.reject(),
      api.getRevenue ? api.getRevenue() : Promise.reject(),
      api.listTenants ? api.listTenants() : Promise.reject(),
      api.getNotificationInbox ? api.getNotificationInbox() : Promise.reject(),
    ]).then((res) => {
      if (!alive) return;
      const val = (r) => (r.status === 'fulfilled' ? r.value : null);
      setStats({
        health: val(res[0]),
        certQueue: val(res[1]),
        deploy: val(res[2]),
        revenue: val(res[3]),
        tenants: val(res[4]),
        notifications: val(res[5]),
      });
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  // ── derive widget values (graceful fallbacks) ─────────────────────────────
  const h = stats?.health || {};
  const certCount = Array.isArray(stats?.certQueue) ? stats.certQueue.length : (stats?.certQueue?.items?.length ?? '—');
  const tenantCount = Array.isArray(stats?.tenants) ? stats.tenants.length : (stats?.tenants?.data?.length ?? '—');
  const alertCount = h.active_alerts ?? '—';
  const deployState = stats?.deploy?.status || stats?.deploy?.overall_status || '—';
  const revenueVal = stats?.revenue?.mrr != null ? `$${Number(stats.revenue.mrr).toLocaleString()}` : (stats?.revenue?.total != null ? `$${Number(stats.revenue.total).toLocaleString()}` : '—');
  const notifCount = Array.isArray(stats?.notifications) ? stats.notifications.filter((n) => !n.read).length : '—';

  const WIDGETS = [
    { icon: Users, label: 'Users', value: h.users ?? '—', to: '/access-control?tab=users', accent: 'text-cyan-400', cap: 'users:read' },
    { icon: Building2, label: 'Workspaces', value: tenantCount, to: '/admin/tenants', accent: 'text-violet-400', cap: 'platform:customers' },
    { icon: Award, label: 'Certification Queue', value: certCount, sub: 'pending review', to: '/admin/certification-queue', accent: 'text-rose-400', cap: 'measures:certify' },
    { icon: Activity, label: 'Platform Health', value: (h.status || '—'), sub: `v${h.platform_version || RELEASE_VERSION}`, to: '/admin/status', accent: 'text-emerald-400', cap: 'platform:operations' },
    { icon: Bell, label: 'System Alerts', value: alertCount, sub: 'active', to: '/alerts', accent: 'text-amber-400', cap: 'alerts:read' },
    { icon: Cpu, label: 'Total Queries', value: (h.total_queries ?? '—'), sub: `${h.total_ai_chats ?? 0} AI chats`, to: '/admin/llm', accent: 'text-sky-400', cap: 'users:read' },
    { icon: DollarSign, label: 'Revenue', value: revenueVal, sub: 'MRR', to: '/admin/revenue', accent: 'text-green-400', cap: 'platform:commercial' },
    { icon: Rocket, label: 'Deploy Status', value: deployState, to: '/admin/deploy-status', accent: 'text-emerald-400', cap: 'platform:operations' },
    { icon: Bell, label: 'Notifications', value: notifCount, sub: 'unread', to: '/settings', accent: 'text-primary' },
  ].filter((w) => capAllowed(w, user, hasPermission));

  // ── search across all visible items ───────────────────────────────────────
  const q = query.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!q) return null;
    const out = [];
    categories.forEach((cat) => {
      cat.items.forEach((it) => {
        if (it.label.toLowerCase().includes(q) || (it.description || '').toLowerCase().includes(q) || cat.label.toLowerCase().includes(q)) {
          out.push({ ...it, categoryLabel: cat.label, categoryId: cat.id });
        }
      });
    });
    return out;
  }, [q, categories]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="administration-hub">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-heading font-bold text-foreground tracking-tight">Administration</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Central hub for platform management — {categories.length} categories available for your role.
        </p>
      </div>

      {/* Search */}
      <div className="relative mb-8 max-w-xl">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search administration functions…"
          className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-card/60 border border-border/40 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary/40"
          data-testid="admin-search-input"
        />
      </div>

      {searchResults ? (
        <div data-testid="admin-search-results">
          <p className="text-xs text-muted-foreground mb-3">{searchResults.length} result(s)</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {searchResults.map((it) => (
              <Link
                key={`${it.categoryId}-${it.label}`}
                to={it.future ? '#' : it.to}
                onClick={(e) => it.future && e.preventDefault()}
                className={`flex items-start gap-3 p-3.5 rounded-xl border border-border/40 bg-card/40 ${it.future ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary/40 hover:bg-card'} transition-all`}
                data-testid={`admin-search-item-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <it.icon className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" strokeWidth={1.75} />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground flex items-center gap-2">
                    {it.label}
                    {it.future && <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted/50 text-muted-foreground">Soon</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground">{it.categoryLabel}</div>
                </div>
              </Link>
            ))}
            {searchResults.length === 0 && (
              <p className="text-sm text-muted-foreground col-span-full py-8 text-center">No functions match &quot;{query}&quot;.</p>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Dashboard widgets */}
          {WIDGETS.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-8" data-testid="admin-widgets">
              {WIDGETS.map((w) => <Widget key={w.label} {...w} loading={loading} />)}
            </div>
          )}

          {/* Quick actions */}
          {quickActions.length > 0 && (
            <div className="mb-8">
              <h2 className="text-sm font-heading font-semibold text-foreground mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" /> Quick actions
              </h2>
              <div className="flex flex-wrap gap-2" data-testid="admin-quick-actions">
                {quickActions.map((a) => (
                  <Link
                    key={a.label}
                    to={a.to}
                    className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-card/60 border border-border/40 text-[13px] font-medium text-foreground hover:border-primary/50 hover:bg-primary/10 transition-all"
                    data-testid={`admin-quick-action-${a.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    <a.icon className="w-3.5 h-3.5 text-primary" strokeWidth={1.75} />
                    {a.label}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Category cards */}
          <h2 className="text-sm font-heading font-semibold text-foreground mb-3">Categories</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-categories">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/administration/${cat.id}`}
                className="group bg-card/60 border border-border/40 rounded-2xl p-5 hover:border-primary/40 hover:bg-card transition-all duration-200"
                data-testid={`admin-category-${cat.id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-11 h-11 rounded-xl bg-muted/40 flex items-center justify-center ${cat.accent}`}>
                    <cat.icon className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                </div>
                <div className="text-base font-heading font-semibold text-foreground">{cat.label}</div>
                <p className="text-[12px] text-muted-foreground mt-1 leading-relaxed">{cat.description}</p>
                <div className="text-[11px] text-muted-foreground/70 mt-3">
                  {cat.items.filter((i) => !i.future).length} tools
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
